import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { makeRequest } from "app/utils/makeRequest";
import { getSessionStore, setSessionStore } from "app/utils/sessionStore";
import { ProgressSpinner } from "primereact/progressspinner";
import { AnimatedCheckbox } from "app/uicomponents/microcomponents/AnimatedChecbox";

import PlanPhasesCard from "app/pages/InteractiveReviewCenter/components/PlanPhasesCard";
import FinancingCard from "app/pages/InteractiveReviewCenter/components/FinancingCard";
import PolicyInformationCard from "../components/PolicyInformationCard";
import PolicyOverviewCard from "../components/PolicyOverviewCard";
import OutsidePledgedAssetsCard from "../components/OutsidePledgedAssetsCard";
import YearStepper, { YearStep } from "app/pages/InteractiveProposal/YearStepper";
import WarningHex from "app/assets/IconComponent/WarningHex";
import ContactsIcon from "app/assets/IconComponent/ContactsIcon";
import { THEME, ProposalTypes } from "app/utils/types";

// ─── API response types ────────────────────────────────────────────────────────

interface PlanReview {
  year: number;
  planYear: number;
  reviewCompleted: string | null;
  confirmedBy: number;
  skippedBy: number;
  total: number;
  reviewOpenDate?: string | null;
  anniversaryDate?: string | null;
}

interface ReviewCenterSummaryData {
  planType: string;
  planSubType: string;
  planEffectiveDate: string;
  proposalNo: string;
  lastValuationDate?: string | null;
  businessValuation?: number | null;
  entity: { title: string; logoUrl: string };
  contacts:
    | Array<{ id?: string; profile: { firstName: string; lastName: string } }>
    | { profile: { firstName: string; lastName: string } };
  shareholders?: Array<{ id?: string; profile: { firstName: string; lastName: string } }>;
  phases: {
    funding: { completed: number; total: number; progress: number };
    growth: { completed: number; total: number; progress: number };
  };
  selectedYear: number;
  selectedCreditType: string;
  planReviews: PlanReview[];
  financing: {
    lender: { logoUrl: string; name: string };
    financingNum: string;
    borrower: { title: string; logoUrl: string };
    interestTreatment: string;
    expectedPayoff: string;
    initialRate: number;
    financingValue: number;
    rateTerms: string;
  };
  policies: Array<{
    insured: { user: { profile: { firstName: string; lastName: string; profilePicture: string } } };
    policyNumber: string;
    policyType?: string;
    policySubType?: string;
    PolicyReview: Array<{
      policyAccountValue: number;
      policySurrenderValue: number;
      policyDB: number;
    }>;
    insuranceCompany: {
      logoPath: string;
      name: string;
      insuranceCompanyBrandings: {
        primaryColor: string;
        primaryColorDark: string;
      };
    };
    beneficiary: string;
  }>;
  outsideAssetsLedger: Array<{
    id: string;
    type: string;
    institution: string;
    accountNumber: string;
    value: number;
    creditPercentage: number;
    creditValue: number;
  }>;
  policyOverview?: {
    shareholders: number;
    lifePolicies: number;
    disabilityPolicies: number;
    shareholderValue: number;
    lifeBenefits: number;
    disabilityBenefits: number;
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatCurrency = (value: number): string => {
  if (value == null) return "—";
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (Math.abs(value) >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return `$${value.toLocaleString()}`;
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const titleCase = (str: string) =>
  str
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatCreditTypeLabel = (ct: string) => (ct === "postCredit" ? "Post-Credit" : "Pre-Credit");

// ─── Map API response to YearStep[] ───────────────────────────────────────────

function mapReviewsToSteps(
  reviews: PlanReview[],
  selectedYear: number,
  selectedCreditType: string,
): YearStep[] {
  return reviews.map((r) => {
    if (r.planYear === selectedYear) {
      return {
        yearNumber: r.planYear,
        calendarYear: r.year,
        status: "CURRENT",
        label: formatCreditTypeLabel(selectedCreditType),
      };
    }
    const storedDate = getSessionStore("currentDate");
    const now = storedDate === true ? new Date(r.reviewOpenDate!).getTime() + 1 : Date.now();
    if (
      r.reviewOpenDate &&
      r.anniversaryDate &&
      (now < new Date(r.reviewOpenDate).getTime() || now > new Date(r.anniversaryDate).getTime())
    ) {
      return { yearNumber: r.planYear, calendarYear: r.year, status: "LOCKED" };
    }
    if (r.confirmedBy > 0 && r.skippedBy >= 0) {
      return {
        yearNumber: r.planYear,
        calendarYear: r.year,
        status: "COMPLETED",
        label: "Confirm(#)",
      };
    }
    if (r.total > 0 && r.confirmedBy === r.total) {
      return {
        yearNumber: r.planYear,
        calendarYear: r.year,
        status: "COMPLETED",
        label: "Confirm All",
      };
    }
    if (r.total > 0 && r.skippedBy === r.total) {
      return {
        yearNumber: r.planYear,
        calendarYear: r.year,
        status: "SKIPPED",
        label: "Skip (All)",
      };
    }
    if (r.confirmedBy > 0) {
      return {
        yearNumber: r.planYear,
        calendarYear: r.year,
        status: "COMPLETED",
        label: `Confirm (${r.confirmedBy})`,
      };
    }
    if (r.skippedBy > 0) {
      return {
        yearNumber: r.planYear,
        calendarYear: r.year,
        status: "SKIPPED",
        label: `Skip (${r.skippedBy})`,
      };
    }
    return { yearNumber: r.planYear, calendarYear: r.year, status: "LOCKED" };
  });
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const ReviewCenterOverview = () => {
  const planId = getSessionStore("planInfo")?.planId;
  // const planId = "43adc439-dbfc-4b7c-a7c8-d1798d9bb7de";
  const sessionPlanType = getSessionStore("planInfo")?.planType;
  const { pathname } = useLocation();
  const isReviewSummary = pathname.includes("interactive-review-center/summary");

  const [selectedYear, setSelectedYear] = useState(1);
  const [selectedCreditType, setSelectedCreditType] = useState("preCredit");
  const [summaryData, setSummaryData] = useState<ReviewCenterSummaryData | null>(null);
  const [loading, setLoading] = useState(false);
  const globalTheme = getSessionStore("globalTheme");
  const isDarkTheme = globalTheme === THEME.DARK;

  useEffect(() => {
    setSessionStore("ircSelectedYear", selectedYear);
  }, [selectedYear]);

  useEffect(() => {
    setSessionStore("ircSelectedCreditType", selectedCreditType);
  }, [selectedCreditType]);

  useEffect(() => {
    if (!planId) return;
    let cancelled = false;

    setLoading(true);
    makeRequest("interactiveReviewCenterSummary")
      .get(`${planId}?year=${selectedYear}&creditType=${selectedCreditType}`, true)
      .then((res: any) => {
        if (cancelled) return;
        const data: ReviewCenterSummaryData = res?.data?.data;
        setSummaryData(data);
      })
      .catch((err: any) => {
        if (!cancelled) console.error("Failed to fetch review center summary:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // eslint-disable-next-line
    return () => {
      cancelled = true;
    };
  }, [planId, selectedYear, selectedCreditType]);

  const yearSteps = useMemo(
    () =>
      summaryData
        ? mapReviewsToSteps(summaryData.planReviews, selectedYear, selectedCreditType)
        : [],
    [summaryData, selectedYear, selectedCreditType],
  );

  const planPhasesProps = summaryData
    ? {
        planType: summaryData.planType,
        planSubType: summaryData.planSubType,
        effectiveDate: formatDate(summaryData.planEffectiveDate),
        progressPercent: summaryData.phases?.funding?.progress,
        lastValuationDate: formatDate(summaryData.lastValuationDate ?? null),
        valuation: summaryData.businessValuation
          ? formatCurrency(summaryData.businessValuation)
          : undefined,
        phases: [
          {
            label: "FUNDING",
            current: summaryData.phases?.funding?.completed ?? 0,
            total: summaryData.phases?.funding?.total ?? 0,
            progress: summaryData.phases?.funding?.progress ?? 0,
            isActive: true,
          },
          {
            label: "GROWTH",
            current: summaryData.phases?.growth?.completed ?? 0,
            total: summaryData.phases?.growth?.total ?? 0,
            progress: summaryData.phases?.growth?.progress ?? 0,
            isActive: false,
          },
        ],
      }
    : undefined;

  const financingRows = summaryData
    ? [
        {
          id: "1",
          lenderName: summaryData.financing.lender.name,
          lenderLogo: summaryData.financing.lender.logoUrl,
          financingNumber: summaryData.financing.financingNum,
          borrowerName: summaryData.financing.borrower.title,
          borrowerLogo: summaryData.financing.borrower.logoUrl,
          interestTreatment: titleCase(summaryData.financing.interestTreatment),
          initialRate: `${summaryData.financing.initialRate}%`,
          financingValue: formatCurrency(summaryData.financing.financingValue),
          expectedPayoff: formatDate(summaryData.financing.expectedPayoff),
        },
      ]
    : undefined;

  const policyRows = summaryData
    ? summaryData.policies.map((p, i) => ({
        id: p.policyNumber || String(i),
        insuredName: `${p.insured.user.profile.firstName} ${p.insured.user.profile.lastName}`,
        insuredAvatar: p.insured.user.profile.profilePicture,
        policyNumber: p.policyNumber,
        policyIcon: p.insuranceCompany.logoPath,
        accountValue: formatCurrency(p.PolicyReview?.[0]?.policyAccountValue),
        surrenderValue: formatCurrency(p.PolicyReview?.[0]?.policySurrenderValue),
        benefit: formatCurrency(p.PolicyReview?.[0]?.policyDB),
        ownerName: summaryData.entity.title,
        ownerLogo: summaryData.entity.logoUrl,
        beneficiary: p.beneficiary,
        primaryColor: p?.insuranceCompany.insuranceCompanyBrandings?.primaryColor,
        primaryColorDark: p?.insuranceCompany.insuranceCompanyBrandings?.primaryColorDark,
        insuranceCompanyLogo: p?.insuranceCompany?.logoPath,
        insuranceCompanyName: p?.insuranceCompany?.name,
        policyType: p.policyType,
        policySubType: p.policySubType,
      }))
    : undefined;

  const assetRows = summaryData
    ? summaryData.outsideAssetsLedger.map((a) => ({
        id: a.id,
        type: titleCase(a.type),
        institution: a.institution,
        accountId: a.accountNumber,
        value: formatCurrency(a.value),
        creditPercent: `${a.creditPercentage}%`,
        creditValue: formatCurrency(a.creditValue),
      }))
    : undefined;

  const policyOverviewData = summaryData?.policyOverview
    ? {
        shareholders: summaryData.policyOverview.shareholders,
        lifePolicies: summaryData.policyOverview.lifePolicies,
        disabilityPolicies: summaryData.policyOverview.disabilityPolicies,
        shareholderValue: formatCurrency(summaryData.policyOverview.shareholderValue),
        lifeBenefits: formatCurrency(summaryData.policyOverview.lifeBenefits),
        disabilityBenefits: formatCurrency(summaryData.policyOverview.disabilityBenefits),
      }
    : undefined;

  return (
    <div className="flex flex-col gap-2 pb-6 mt-[180px] relative">
      {loading && (
        <div className="flex justify-center py-8">
          <ProgressSpinner style={{ width: 40, height: 40 }} />
        </div>
      )}

      {isReviewSummary && (
        <YearStepper
          isDark={isDarkTheme}
          steps={yearSteps}
          phases={["preCredit", "postCredit"]}
          selectedPhase={selectedCreditType}
          onYearClick={(step) => setSelectedYear(step.yearNumber)}
          onPhaseClick={(phase) => {
            setSelectedCreditType(phase);
            setSessionStore("ircSelectedCreditType", phase);
            window.dispatchEvent(
              new CustomEvent("ircSelectionChanged", { detail: { creditType: phase } }),
            );
          }}
        />
      )}

      {sessionPlanType === ProposalTypes.BuySell ? (
        <div className="flex gap-4 w-full">
          <PlanPhasesCard {...(planPhasesProps ?? {})} />

          {/* Shareholders card */}
          <div
            className={`w-1/2 rounded-2xl overflow-hidden shadow-lg ${
              isDarkTheme ? "bg-[#2A2A3D]" : "bg-white"
            }`}
          >
            <p
              className={`text-[13px] p-4 font-semibold conthrax-font flex items-center gap-2 mb-3 ${
                isDarkTheme ? "bg-[#1E1E2E] text-[#93C5FD]" : "bg-[#F5F5F5] text-[#1E3A8A]"
              }`}
            >
              <ContactsIcon fill={isDarkTheme ? "#93C5FD" : "#1E3A8A"} width="21px" height="21px" />
              Shareholders
            </p>
            <div className="flex flex-wrap gap-2 px-4 pb-4 max-h-[200px] overflow-y-auto">
              {summaryData?.shareholders && summaryData.shareholders.length > 0 ? (
                summaryData.shareholders.map((s, i) => (
                  <AnimatedCheckbox
                    key={s.id ?? String(i)}
                    checked
                    onChange={() => {}}
                    inputId={s.id ?? String(i)}
                    label={`${s.profile.firstName} ${s.profile.lastName}`}
                    brandingColor="#004781"
                    size={20}
                  />
                ))
              ) : (
                <p className="text-[15px] text-gray-400">No shareholders available</p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <PlanPhasesCard {...(planPhasesProps ?? {})} />
      )}

      {sessionPlanType !== ProposalTypes.BuySell && <FinancingCard rows={financingRows} />}

      {sessionPlanType === ProposalTypes.BuySell && (
        <PolicyOverviewCard data={policyOverviewData} />
      )}

      <PolicyInformationCard policies={policyRows} />

      {sessionPlanType !== ProposalTypes.SplitDollar &&
        sessionPlanType !== ProposalTypes.BuySell && (
          <OutsidePledgedAssetsCard assets={assetRows} />
        )}

      <div className="bg-[#F5F9FF] border border-[#ABC9FB] rounded-2xl p-2 flex gap-2 items-center mt-4">
        <WarningHex width="16" fill="#326FD1" />
        <span className="text-[#326FD1] text-[12px]">
          Data shown is based on the latest available inputs. Always confirm directly with the
          insurance carrier. See Disclaimer Page for details.
        </span>
      </div>
    </div>
  );
};

export default ReviewCenterOverview;
